import { dangerouslyInjectHTML } from '@visiba/react-std';
import { ForwardedRef, forwardRef } from 'react';
import styled from 'styled-components';
import { coreTokens } from '../../core';
import { HeadlessIcon, HeadlessIconProps, Icon } from '../icon';
import loadedFlagSet from './flag_set.json';

export interface ExcludedFlagDictionary {
	ad: string;
	af: string;
	bt: string;
	bz: string;
	do: string;
	ec: string;
	es: string;
	fj: string;
	gt: string;
	hr: string;
	ht: string;
	kz: string;
	lk: string;
	md: string;
	me: string;
	mx: string;
	pt: string;
	py: string;
	rs: string;
	sm: string;
	sv: string;
	tm: string;
	va: string;
	vg: string;
}

export interface FlagDictionary {
	ae: string;
	ag: string;
	al: string;
	am: string;
	ao: string;
	ar: string;
	at: string;
	au: string;
	az: string;
	ba: string;
	bb: string;
	bd: string;
	be: string;
	bf: string;
	bg: string;
	bh: string;
	bi: string;
	bj: string;
	bn: string;
	bo: string;
	br: string;
	bs: string;
	bw: string;
	by: string;
	ca: string;
	cd: string;
	cf: string;
	cg: string;
	ch: string;
	ci: string;
	ck: string;
	cl: string;
	cm: string;
	cn: string;
	co: string;
	cr: string;
	cu: string;
	cv: string;
	cy: string;
	cz: string;
	de: string;
	dj: string;
	dk: string;
	dm: string;
	dz: string;
	ee: string;
	eg: string;
	er: string;
	et: string;
	fi: string;
	fm: string;
	fr: string;
	ga: string;
	gb: string;
	gd: string;
	ge: string;
	gh: string;
	gm: string;
	gn: string;
	gq: string;
	gr: string;
	gw: string;
	gy: string;
	hk: string;
	hn: string;
	hu: string;
	id: string;
	ie: string;
	il: string;
	in: string;
	iq: string;
	ir: string;
	is: string;
	it: string;
	jm: string;
	jo: string;
	jp: string;
	ke: string;
	kg: string;
	kh: string;
	ki: string;
	km: string;
	kn: string;
	kp: string;
	kr: string;
	kw: string;
	la: string;
	lb: string;
	lc: string;
	li: string;
	lr: string;
	ls: string;
	lt: string;
	lu: string;
	lv: string;
	ly: string;
	ma: string;
	mc: string;
	mg: string;
	mh: string;
	mk: string;
	ml: string;
	mm: string;
	mn: string;
	mr: string;
	mt: string;
	mu: string;
	mv: string;
	mw: string;
	my: string;
	mz: string;
	na: string;
	ne: string;
	ng: string;
	ni: string;
	nl: string;
	no: string;
	np: string;
	nr: string;
	nu: string;
	nz: string;
	om: string;
	pa: string;
	pe: string;
	pg: string;
	ph: string;
	pk: string;
	pl: string;
	pw: string;
	qa: string;
	ro: string;
	ru: string;
	rw: string;
	sa: string;
	sb: string;
	sc: string;
	sd: string;
	se: string;
	sg: string;
	si: string;
	sk: string;
	sl: string;
	sn: string;
	so: string;
	sr: string;
	ss: string;
	st: string;
	sy: string;
	sz: string;
	td: string;
	tg: string;
	th: string;
	tj: string;
	tl: string;
	tn: string;
	to: string;
	tr: string;
	tt: string;
	tv: string;
	tw: string;
	tz: string;
	ua: string;
	ug: string;
	us: string;
	uy: string;
	uz: string;
	vc: string;
	ve: string;
	vn: string;
	ws: string;
	xk: string;
	ye: string;
	za: string;
	zm: string;
	zw: string;
}

type FlagMap = {
	[key in keyof FlagDictionary]: {
		innerHTML: string;
		viewBox: string;
	};
};

export type FlagType = keyof FlagDictionary;
export type ExcludedFlagType = keyof ExcludedFlagDictionary;

const FlagMap = loadedFlagSet as unknown as FlagMap;

const StyledFlag = styled(HeadlessIcon)`
	--width: ${coreTokens.size.x2};
	--height: ${coreTokens.size.x2};

	stroke: none;
	width: var(--width);
	height: var(--height);

	&[data-size='small'] {
		--width: ${coreTokens.size.x2};
		--height: ${coreTokens.size.x2};
	}
	&[data-size='medium'] {
		--width: ${coreTokens.size.x3};
		--height: ${coreTokens.size.x3};
	}
	&[data-size='large'] {
		--width: ${coreTokens.size.x4};
		--height: ${coreTokens.size.x4};
	}
`;

interface CellulaFlagProps extends Omit<HeadlessIconProps, 'viewBox'> {
	flag: FlagType;
	size: 'small' | 'medium' | 'large';
}

function CellulaFlag({ flag, size, ...rest }: CellulaFlagProps, ref: ForwardedRef<SVGSVGElement>): JSX.Element {
	const selectedFlag = FlagMap[flag];

	if (!selectedFlag) {
		return <Icon type='network' size={size} />;
	}

	return (
		<StyledFlag
			viewBox={selectedFlag.viewBox}
			dangerouslySetInnerHTML={dangerouslyInjectHTML(selectedFlag.innerHTML)}
			{...rest}
			data-size={size}
			ref={ref}
		/>
	);
}

const Flag = forwardRef(CellulaFlag);

export type { CellulaFlagProps as FlagProps };
export { Flag };
